// @ts-check
// Etapa 1 da importação (doc 14, 9.2 "upload"): escolha do tipo de planilha, arquivo por clique ou arrastar e
// modelos para baixar.
import { html } from 'htm/preact';
import { useRef, useState } from 'preact/hooks';
import { Icon } from '../../ui/icon.js';
import { Button } from '../../ui/button.js';
import { buildCsv, downloadCsv, toFileName } from '../../lib/csv.js';
import { t, translateMetadataLabel } from '../../i18n/index.js';
import { MAXIMUM_IMPORT_ROWS, buildSampleRows, readSpreadsheetFile } from './import-model.js';

const ACCEPTED_FILES = '.csv,.tsv,.txt,.xlsx,.xls,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

/**
 * @param {{ objectMetadata: import('../../lib/field-metadata.js').ObjectMetadata, mode: import('./import-model.js').ImportMode,
 *   canUseFamilyTemplate: boolean, targets: import('./import-model.js').ImportTarget[], onModeChange: (mode: import('./import-model.js').ImportMode) => void,
 *   onLoaded: (file: { name: string, sheets: import('./import-model.js').Sheet[] }) => void }} props
 */
export function UploadStep({ objectMetadata, mode, canUseFamilyTemplate, targets, onModeChange, onLoaded }) {
  const inputRef = useRef(/** @type {HTMLInputElement | null} */ (null));
  const [isDragging, setIsDragging] = useState(false);
  const [isReading, setIsReading] = useState(false);
  const [error, setError] = useState(/** @type {string | null} */ (null));
  const objectLabel = translateMetadataLabel(objectMetadata.labelPlural);

  const readFile = async (/** @type {File | undefined | null} */ file) => {
    if (!file) return;
    setError(null);
    setIsReading(true);
    try {
      onLoaded({ name: file.name, sheets: await readSpreadsheetFile(file) });
    } catch (readError) {
      setError(readError instanceof Error ? readError.message : t('spreadsheetImport.upload.unreadable'));
    } finally {
      setIsReading(false);
    }
  };

  const downloadSample = () => {
    const name = mode === 'family'
      ? t('spreadsheetImport.upload.familySampleFileName')
      : t('spreadsheetImport.upload.sampleFileName', { object: objectLabel });
    downloadCsv(buildCsv(buildSampleRows(mode, targets)), toFileName(name));
  };

  return html`<div class="spreadsheet-import-upload">
    ${canUseFamilyTemplate
      ? html`<fieldset class="spreadsheet-import-modes">
          <legend class="spreadsheet-import-section-label">${t('spreadsheetImport.modes.label')}</legend>
          ${[
            ['object', t('spreadsheetImport.modes.object', { object: objectLabel }), t('spreadsheetImport.modes.objectHint', { object: objectLabel }), objectMetadata.icon ?? 'IconTable'],
            ['family', t('spreadsheetImport.modes.family'), t('spreadsheetImport.modes.familyHint'), 'IconUsersGroup'],
          ].map(([value, title, hint, icon]) => html`<label class="spreadsheet-import-mode" data-selected=${mode === value ? 'true' : undefined} key=${value}>
            <input type="radio" name="spreadsheet-import-mode" value=${value} checked=${mode === value}
              onChange=${() => onModeChange(/** @type {import('./import-model.js').ImportMode} */ (value))} />
            <${Icon} name=${icon} size=${18} />
            <span class="spreadsheet-import-mode-text">
              <span class="spreadsheet-import-mode-title">${title}</span>
              <span class="spreadsheet-import-mode-hint">${hint}</span>
            </span>
          </label>`)}
        </fieldset>`
      : null}
    <div
      class="spreadsheet-import-dropzone"
      data-dragging=${isDragging ? 'true' : undefined}
      onDragOver=${(/** @type {DragEvent} */ event) => {
        event.preventDefault();
        setIsDragging(true);
      }}
      onDragLeave=${() => setIsDragging(false)}
      onDrop=${(/** @type {DragEvent} */ event) => {
        event.preventDefault();
        setIsDragging(false);
        readFile(event.dataTransfer?.files?.[0]);
      }}
    >
      <${Icon} name="IconFileSpreadsheet" size=${32} />
      <div class="spreadsheet-import-dropzone-title">${isReading ? t('spreadsheetImport.upload.processing') : t('spreadsheetImport.upload.dropTitle')}</div>
      <div class="spreadsheet-import-dropzone-subtitle">${t('spreadsheetImport.upload.dropSubtitle', { count: MAXIMUM_IMPORT_ROWS.toLocaleString('pt-BR') })}</div>
      <input ref=${inputRef} type="file" accept=${ACCEPTED_FILES} hidden
        onChange=${(/** @type {Event} */ event) => {
          const input = /** @type {HTMLInputElement} */ (event.currentTarget);
          readFile(input.files?.[0]);
          input.value = '';
        }} />
      <${Button} title=${t('spreadsheetImport.upload.select')} icon="IconUpload" size="small" accent="blue" isLoading=${isReading}
        onClick=${() => inputRef.current?.click()} />
    </div>
    ${error ? html`<div class="spreadsheet-import-banner" data-variant="error" role="alert"><${Icon} name="IconAlertTriangle" size=${16} />${error}</div>` : null}
    <div class="spreadsheet-import-sample">
      <${Button} title=${mode === 'family' ? t('spreadsheetImport.upload.downloadFamilySample') : t('spreadsheetImport.upload.downloadSample')}
        icon="IconDownload" size="small" variant="secondary" onClick=${downloadSample} />
    </div>
  </div>`;
}
